import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../../axiosInstance";

interface PreviewState {
  data: any;
  loading: boolean;
  error: string | null;
  fieldDropdown: any;
  fieldDropdownLoading: boolean;
  fieldDropdownError: string | null;
  audienceResponse: any;
  audienceLoading: boolean;
  audienceError: string | null;
  listOfAudiences: any;
  listOfAudiencesLoading: boolean;
  listOfAudiencesError: string | null;
  sendAudienceResponse: any;
  sendAudienceLoading: boolean;
  sendAudienceError: string | null;
  folderAssignResponse: any;
  folderAssignLoading: boolean;
  folderAssignError: string | null;
  listOfCampaigns: any;
  listOfCampaignsLoading: boolean;
  listOfCampaignsError: string | null;
}

const initialState: PreviewState = {
  data: null,
  loading: false,
  error: null,
  fieldDropdown: null,
  fieldDropdownLoading: false,
  fieldDropdownError: null,
  audienceResponse: null,
  audienceLoading: false,
  audienceError: null,
  listOfAudiences: null,
  listOfAudiencesLoading: false,
  listOfAudiencesError: null,
  sendAudienceResponse: null,
  sendAudienceLoading: false,
  sendAudienceError: null,
  folderAssignResponse: null,
  folderAssignLoading: false,
  folderAssignError: null,
  listOfCampaigns: null,
  listOfCampaignsLoading: false,
  listOfCampaignsError: null,
};

export const fetchPreviewData = createAsyncThunk(
  "preview/fetchData",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/preview_count");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);
export const fetchFieldDropdownValues = createAsyncThunk(
  "preview/fetchDropdownValues",
  async (tableName: string, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/value_dropdown", {
        table_name: tableName,
      });
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const createAudience = createAsyncThunk(
  "preview/createAudience",
  async (payload, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/audience", payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const sendAudience = createAsyncThunk(
  "preview/sendAudience",
  async (payload: { audience_name: string }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post("/send_audience", payload);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const listOfAudiences = createAsyncThunk(
  "preview/listOfAudiences",
  async (_, { rejectWithValue }) => {
    try {
      let response = await axiosInstance.post("/folder_dropdown");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const folder_assign = createAsyncThunk(
  "preview/folder_assign",
  async (_, { rejectWithValue }) => {
    try {
      let response = await axiosInstance.post("/folder_assign");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

export const listOfCampaigns = createAsyncThunk(
  "preview/campaign_dropdown",
  async (_, { rejectWithValue }) => {
    try {
      let response = await axiosInstance.post("/campaign_dropdown");
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.message);
    }
  }
);

const previewSlice = createSlice({
  name: "preview",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchPreviewData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPreviewData.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload;
      })
      .addCase(fetchPreviewData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      .addCase(createAudience.pending, (state) => {
        state.audienceLoading = true;
        state.audienceError = null;
      })
      .addCase(createAudience.fulfilled, (state, action) => {
        state.audienceLoading = false;
        state.audienceResponse = action.payload;
      })
      .addCase(createAudience.rejected, (state, action) => {
        state.audienceLoading = false;
        state.audienceError = action.payload as string;
      })

      .addCase(listOfAudiences.pending, (state) => {
        state.listOfAudiencesLoading = true;
        state.listOfAudiencesError = null;
      })
      .addCase(listOfAudiences.fulfilled, (state, action) => {
        state.listOfAudiencesLoading = false;
        try {
          state.listOfAudiences = JSON.parse(action.payload);
        } catch (err) {
          console.error("Failed to parse listOfAudiences payload", err);
          state.listOfAudiences = [];
        }
      })
      .addCase(listOfAudiences.rejected, (state, action) => {
        state.listOfAudiencesLoading = false;
        state.listOfAudiencesError = action.payload as string;
      })

      .addCase(sendAudience.pending, (state) => {
        state.sendAudienceLoading = true;
        state.sendAudienceError = null;
      })
      .addCase(sendAudience.fulfilled, (state, action) => {
        state.sendAudienceLoading = false;
        state.sendAudienceResponse = action.payload;
      })
      .addCase(sendAudience.rejected, (state, action) => {
        state.sendAudienceLoading = false;
        state.sendAudienceError = action.payload as string;
      })

      .addCase(folder_assign.pending, (state) => {
        state.folderAssignLoading = true;
        state.folderAssignError = null;
      })
      .addCase(folder_assign.fulfilled, (state, action) => {
        state.folderAssignLoading = false;
        try {
          state.folderAssignResponse =
            typeof action.payload === "string" ? JSON.parse(action.payload) : action.payload || [];
        } catch (err) {
          console.error("Failed to parse folderAssignResponse payload", err);
          state.folderAssignResponse = [];
        }
      })
      .addCase(folder_assign.rejected, (state, action) => {
        state.folderAssignLoading = false;
        state.folderAssignError = action.payload as string;
      })
      .addCase(listOfCampaigns.pending, (state) => {
        state.listOfCampaignsLoading = true;
        state.listOfCampaignsError = null;
      })
      .addCase(listOfCampaigns.fulfilled, (state, action) => {
        state.listOfCampaignsLoading = false;
        try {
          state.listOfCampaigns = JSON.parse(action.payload);
        } catch (err) {
          console.error("Failed to parse listOfCampaigns payload", err);
          state.listOfCampaigns = [];
        }
      })
      .addCase(listOfCampaigns.rejected, (state, action) => {
        state.listOfCampaignsLoading = false;
        state.listOfCampaignsError = action.payload as string;
      });
  },
});

export default previewSlice.reducer;
